import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { ShoppingCart, User, LogOut, Menu as MenuIcon, Package, Utensils, Bell, Check, X, MapPin } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import BranchSelectionModal from './BranchSelectionModal';
import { cleanLogoUrl } from '../utils/logo';

export default function Navbar() {
    const { user, logout, hasFeatureAccess } = useAuth();
    const { tenant, isFranchise, franchise, branches } = useTenant();
    const { itemCount } = useCart();
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDesktopNotifOpen, setIsDesktopNotifOpen] = useState(false);
    const [isMobileNotifOpen, setIsMobileNotifOpen] = useState(false);
    const [showBranchModal, setShowBranchModal] = useState(false);
    const desktopNotifRef = useRef(null);
    const mobileNotifRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const hamburgerRef = useRef(null);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallBtn, setShowInstallBtn] = useState(true);


    useEffect(() => {
        // Detect if the app is currently running in standalone display mode (as an installed PWA app)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                             window.navigator.standalone === true || 
                             document.referrer.includes('android-app://');

        if (isStandalone) {
            setShowInstallBtn(false);
            return;
        }

        // Check if beforeinstallprompt already fired (before React mounted)
        if (window.__pwaInstallPrompt) {
            setDeferredPrompt(window.__pwaInstallPrompt);
        }

        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            window.__pwaInstallPrompt = e;
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        setIsMenuOpen(false);

        // 1. If we captured beforeinstallprompt in React state, use it directly
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                console.log('[PWA] Install outcome:', outcome);
                setDeferredPrompt(null);
                if (outcome === 'accepted') {
                    setShowInstallBtn(false);
                }
            } catch (err) {
                console.warn('[PWA] prompt() failed:', err);
            }
            return;
        }

        // 2. If inside iframe (custom template), delegate to parent window
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'TRIGGER_INSTALL' }, '*');
            return;
        }

        // 3. Fallback instructions for iOS / browsers that don't support beforeinstallprompt
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isIOS) {
            alert("To install this app:\n\n1. Tap the Share button (↑) in Safari\n2. Scroll down and tap 'Add to Home Screen'\n3. Tap 'Add'");
        } else {
            alert("To install this app:\n\n1. Tap the ⋮ menu (3 dots) in Chrome\n2. Tap 'Install app'\n3. Confirm the installation");
        }
    };

    // Close notification dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (desktopNotifRef.current && !desktopNotifRef.current.contains(event.target)) {
                setIsDesktopNotifOpen(false);
            }
            if (mobileNotifRef.current && !mobileNotifRef.current.contains(event.target)) {
                setIsMobileNotifOpen(false);
            }
            if (mobileMenuRef.current && 
                !mobileMenuRef.current.contains(event.target) && 
                hamburgerRef.current && 
                !hamburgerRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        window.location.href = window.location.origin;
    };

    const getNotificationLink = (notif) => {
        if (notif.type === 'reservation') {
            if (user.role === 'admin' || user.role === 'restaurant_admin') {
                return '/admin/reservations';
            }
        }
        if (!notif.order_id) return null;
        if (user.role === 'admin') return '/admin/orders';
        if (user.role === 'kitchen_staff') return '/kitchen';
        if (user.role === 'delivery_partner') return '/delivery';
        return '/orders';
    };

    const getHomeLink = () => {
        if (isFranchise && franchise?.slug) {
            return `/?restaurant=${franchise.slug}`;
        }
        if (location.pathname.startsWith('/branch/')) {
            const parts = location.pathname.split('/');
            if (parts[2]) {
                return `/branch/${parts[2]}`;
            }
        }
        const activeBranch = localStorage.getItem('active_branch_slug');
        if (activeBranch) {
            return `/branch/${activeBranch}`;
        }
        if (tenant?.slug) {
            return `/?restaurant=${tenant.slug}`;
        }
        return '/';
    };

    return (
        <nav className="bg-white shadow-md sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    <div className="flex items-center min-w-0">
                        <Link to={getHomeLink()} className="text-base sm:text-lg md:text-2xl font-extrabold text-primary-600 flex items-center gap-1.5 tracking-tighter min-w-0 py-1">
                            {isFranchise && franchise ? (
                                <>
                                    {franchise.logo_url ? (
                                        <img src={cleanLogoUrl(franchise.logo_url)} alt={franchise.name} className="h-10 sm:h-12 w-auto object-contain flex-shrink-0" />
                                    ) : (
                                        <div className="bg-primary-100 p-1 rounded-lg flex-shrink-0 text-xs sm:text-sm">🍽️</div>
                                    )}
                                    <span className="uppercase truncate max-w-[100px] xs:max-w-[150px] sm:max-w-[200px] md:max-w-none" style={{ WebkitTextStroke: 'var(--text-stroke-navbar, none)' }}>{franchise.name}</span>
                                </>
                            ) : tenant?.logo_url ? (
                                <>
                                    <img src={cleanLogoUrl(tenant.logo_url)} alt={tenant.name} className="h-10 sm:h-12 w-auto object-contain flex-shrink-0" />
                                    <span className="uppercase truncate max-w-[100px] xs:max-w-[150px] sm:max-w-[200px] md:max-w-none" style={{ WebkitTextStroke: 'var(--text-stroke-navbar, none)' }}>{tenant.name}</span>
                                    {/* Subtitle / Branch indicator */}
                                </>
                            ) : (
                                <>
                                    <div className="bg-primary-100 p-1 rounded-lg flex-shrink-0 text-xs sm:text-sm">🍽️</div>
                                    <span className="uppercase truncate max-w-[100px] xs:max-w-[150px] sm:max-w-[200px] md:max-w-none" style={{ WebkitTextStroke: 'var(--text-stroke-navbar, none)' }}>{tenant?.name || 'SavoryBites'}</span>
                                </>
                            )}
                        </Link>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center space-x-8">
                        {user ? (
                            <>
                                {user.role === 'admin' && (
                                    <>
                                        <Link to="/menu" className="text-gray-700 hover:text-primary-600 font-medium">Menu</Link>
                                        <Link to="/admin" className="text-gray-700 hover:text-primary-600 font-medium">Admin Panel</Link>
                                    </>
                                )}
                                {user.role === 'kitchen_staff' && hasFeatureAccess('kitchen_dashboard') && (
                                    <>
                                        <Link to="/menu" className="text-gray-700 hover:text-primary-600 font-medium">Menu</Link>
                                        <Link to="/kitchen" className="text-gray-700 hover:text-primary-600 font-medium">Kitchen Dash</Link>
                                    </>
                                )}
                                {user.role === 'delivery_partner' && hasFeatureAccess('delivery_dashboard') && (
                                    <Link to="/delivery" className="text-gray-700 hover:text-primary-600 font-medium">Delivery Dash</Link>
                                )}
                                {(user.role === 'customer' || !user.role) && (
                                    <>
                                        {isFranchise && branches && branches.length > 0 && (
                                            <button
                                                onClick={() => setShowBranchModal(true)}
                                                className="flex items-center gap-1 text-xs font-bold uppercase bg-slate-100 hover:bg-primary-50 text-slate-700 hover:text-primary-600 px-3 py-1.5 rounded-xl border border-slate-200 transition-colors"
                                            >
                                                <MapPin className="w-3.5 h-3.5 text-primary-600" />
                                                <span>Select Branch</span>
                                            </button>
                                        )}
                                        <Link to="/menu" className="text-gray-700 hover:text-primary-600 font-medium">Menu</Link>
                                        <Link to="/cart" className="relative text-gray-700 hover:text-primary-600">
                                            <ShoppingCart className="w-6 h-6" />
                                            {itemCount > 0 && (
                                                <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                    {itemCount}
                                                </span>
                                            )}
                                        </Link>
                                        <Link to="/orders" className="text-gray-700 hover:text-primary-600 font-medium flex items-center space-x-1">
                                            <Package className="w-5 h-5" />
                                            <span>My Orders</span>
                                        </Link>
                                    </>
                                )}

                                {/* Notification Bell */}
                                <div className="relative" ref={desktopNotifRef}>
                                    <button
                                        onClick={() => setIsDesktopNotifOpen(!isDesktopNotifOpen)}
                                        className="relative text-gray-700 hover:text-primary-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                                    >
                                        <Bell className="w-6 h-6" />
                                        {unreadCount > 0 && (
                                            <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center border-2 border-white">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </button>

                                    {/* Notification Dropdown */}
                                    {isDesktopNotifOpen && (
                                        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                                            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                                <h3 className="font-bold text-gray-900">Notifications</h3>
                                                {unreadCount > 0 && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markAllAsRead();
                                                        }}
                                                        className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                                                    >
                                                        Mark all as read
                                                    </button>
                                                )}
                                            </div>
                                            <div className="max-h-96 overflow-y-auto">
                                                {notifications.length > 0 ? (
                                                    notifications.map((notif) => (
                                                        <div
                                                            key={notif.id}
                                                            className={`p-4 border-b border-gray-50 flex gap-3 hover:bg-gray-50 cursor-pointer transition-colors relative ${!notif.is_read ? 'bg-primary-50/30' : ''}`}
                                                            onClick={() => {
                                                                markAsRead(notif.id);
                                                                const link = getNotificationLink(notif);
                                                                console.log('Navigating to:', link, 'for role:', user.role);
                                                                if (link) navigate(link);
                                                                setIsDesktopNotifOpen(false);
                                                            }}
                                                        >
                                                            <div className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${!notif.is_read ? 'bg-primary-600' : 'bg-transparent'}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-semibold text-gray-900 truncate ${!notif.is_read ? '' : 'text-gray-600'}`}>
                                                                    {notif.title}
                                                                </p>
                                                                <p className="text-sm text-gray-600 mt-0.5 line-clamp-2 leading-relaxed">
                                                                    {notif.message}
                                                                </p>
                                                                <p className="text-[10px] text-gray-400 mt-2 font-medium">
                                                                    {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-10 text-center text-gray-500">
                                                        <Bell className="w-8 h-8 mx-auto mb-3 opacity-20" />
                                                        <p className="text-sm">No notifications yet</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <Link to="/profile" className="flex items-center space-x-2 ml-4 hover:text-primary-600 transition-colors">
                                    <User className="w-5 h-5 text-gray-600" />
                                    <span className="text-gray-700 font-medium">{user.full_name}</span>
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="p-1 text-gray-700 hover:text-red-600 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <LogOut className="w-5 h-5" />
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center space-x-4">
                                <Link to="/menu" className="text-gray-700 hover:text-primary-600 font-medium">Menu</Link>
                                <Link to="/login" className="text-gray-700 hover:text-primary-600 font-medium">
                                    Login
                                </Link>
                                <Link to="/register" className="btn-primary">
                                    Sign Up
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden flex items-center space-x-2 sm:space-x-3">
                        {user ? (
                            <>
                                <div className="relative" ref={mobileNotifRef}>
                                    <button
                                        onClick={() => setIsMobileNotifOpen(!isMobileNotifOpen)}
                                        className="relative text-gray-700 hover:text-primary-600 transition-colors p-1 rounded-full hover:bg-gray-100 flex items-center"
                                    >
                                        <Bell className="w-6 h-6" />
                                        {unreadCount > 0 && (
                                            <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center border-2 border-white">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </button>

                                    {/* Mobile Notification Dropdown */}
                                    {isMobileNotifOpen && (
                                        <div className="fixed inset-x-4 top-16 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200 lg:absolute lg:inset-auto lg:right-0 lg:w-80">
                                            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                                <h3 className="font-bold text-gray-900">Notifications</h3>
                                                {unreadCount > 0 && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markAllAsRead();
                                                        }}
                                                        className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                                                    >
                                                        Mark all as read
                                                    </button>
                                                )}
                                            </div>
                                            <div className="max-h-[60vh] overflow-y-auto">
                                                {notifications.length > 0 ? (
                                                    notifications.map((notif) => (
                                                        <div
                                                            key={notif.id}
                                                            className={`p-4 border-b border-gray-50 flex gap-3 hover:bg-gray-50 cursor-pointer transition-colors relative ${!notif.is_read ? 'bg-primary-50/30' : ''}`}
                                                            onClick={() => {
                                                                markAsRead(notif.id);
                                                                const link = getNotificationLink(notif);
                                                                console.log('Mobile navigating to:', link, 'for role:', user.role);
                                                                if (link) navigate(link);
                                                                setIsMobileNotifOpen(false);
                                                                setIsMenuOpen(false);
                                                            }}
                                                        >
                                                            <div className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${!notif.is_read ? 'bg-primary-600' : 'bg-transparent'}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-semibold text-gray-900 truncate ${!notif.is_read ? '' : 'text-gray-600'}`}>
                                                                    {notif.title}
                                                                </p>
                                                                <p className="text-sm text-gray-600 mt-0.5 line-clamp-2 leading-relaxed">
                                                                    {notif.message}
                                                                </p>
                                                                <p className="text-[10px] text-gray-400 mt-2 font-medium">
                                                                    {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-10 text-center text-gray-500">
                                                        <Bell className="w-8 h-8 mx-auto mb-3 opacity-20" />
                                                        <p className="text-sm">No notifications yet</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Link 
                                    to={
                                        user.role === 'super_admin' ? '/super-admin' :
                                        ['admin', 'restaurant_admin'].includes(user.role) ? '/admin' :
                                        user.role === 'kitchen_staff' ? '/kitchen' :
                                        user.role === 'delivery_partner' ? '/delivery' : '/profile'
                                    }
                                    className="text-gray-700 hover:text-primary-600 transition-colors p-1 rounded-full hover:bg-gray-100 flex items-center"
                                >
                                    <User className="w-6 h-6" />
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="text-gray-700 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-gray-100 flex items-center"
                                >
                                    <LogOut className="w-6 h-6" />
                                </button>
                            </>
                        ) : (
                            <Link to="/login" className="px-3.5 py-1.5 text-xs font-semibold text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors">
                                Login
                            </Link>
                        )}

                        <Link to="/menu" className="text-gray-800 hover:text-primary-600 font-extrabold text-xs uppercase tracking-widest px-2 py-1.5 transition-colors">
                            Menu
                        </Link>
                        <button ref={hamburgerRef} onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-1 text-gray-700 hover:text-primary-600 rounded-full transition-colors flex items-center">
                            <MenuIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div ref={mobileMenuRef} className="md:hidden bg-white border-t">
                    <div className="px-2 pt-2 pb-3 space-y-1">
                        {showInstallBtn && (
                            ((!user && hasFeatureAccess('customer_app')) || 
                             (user && user.role === 'customer' && hasFeatureAccess('customer_app')) ||
                             (user && user.role === 'admin' && hasFeatureAccess('admin_app')) ||
                             (user && user.role === 'kitchen_staff' && hasFeatureAccess('kitchen_app')) ||
                             (user && user.role === 'delivery_partner' && hasFeatureAccess('delivery_app'))
                            ) && (
                                <button
                                    onClick={handleInstallClick}
                                    className="block w-full text-left px-3 py-2 text-primary-600 font-semibold hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    <span>Download App</span>
                                </button>
                            )
                        )}
                        {user ? (
                            <>
                                {user.role === 'admin' && (
                                    <Link onClick={() => setIsMenuOpen(false)} to="/admin" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">Admin Panel</Link>
                                )}
                                {user.role === 'kitchen_staff' && hasFeatureAccess('kitchen_dashboard') && (
                                    <Link onClick={() => setIsMenuOpen(false)} to="/kitchen" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">Kitchen Dash</Link>
                                )}
                                {user.role === 'delivery_partner' && hasFeatureAccess('delivery_dashboard') && (
                                    <Link onClick={() => setIsMenuOpen(false)} to="/delivery" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">Delivery Dash</Link>
                                )}
                                {(user.role === 'customer' || !user.role) && (
                                    <>
                                        <Link onClick={() => setIsMenuOpen(false)} to="/cart" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">
                                            Cart ({itemCount})
                                        </Link>
                                        <Link onClick={() => setIsMenuOpen(false)} to="/orders" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">
                                            My Orders
                                        </Link>
                                        <Link onClick={() => setIsMenuOpen(false)} to="/profile" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">
                                            My Profile
                                        </Link>
                                    </>
                                )}
                                <button
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        handleLogout();
                                    }}
                                    className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-50"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <Link onClick={() => setIsMenuOpen(false)} to="/login" className="block px-3 py-2 text-gray-700 hover:bg-gray-50">
                                    Login
                                </Link>
                                <Link onClick={() => setIsMenuOpen(false)} to="/register" className="block px-3 py-2 text-primary-600 font-medium">
                                    Sign Up
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Customer Mobile Bottom Navigation - Show only when logged in */}
            {(user && user.role === 'customer') && (
                <nav className="md:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-50 safe-area-bottom">
                    <div className="flex justify-between items-center h-16 px-6">
                        <Link
                            to="/menu"
                            className={`flex flex-col items-center space-y-1 ${location.pathname === '/menu' ? 'text-primary-600' : 'text-gray-500'}`}
                        >
                            <Utensils className="w-6 h-6" />
                            <span className="text-[10px] font-medium">Menu</span>
                        </Link>

                        <Link
                            to="/cart"
                            className={`relative flex flex-col items-center space-y-1 ${location.pathname === '/cart' ? 'text-primary-600' : 'text-gray-500'}`}
                        >
                            <ShoppingCart className="w-6 h-6" />
                            {itemCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                    {itemCount}
                                </span>
                            )}
                            <span className="text-[10px] font-medium">Cart</span>
                        </Link>

                        <Link
                            to="/orders"
                            className={`flex flex-col items-center space-y-1 ${location.pathname === '/orders' ? 'text-primary-600' : 'text-gray-500'}`}
                        >
                            <Package className="w-6 h-6" />
                            <span className="text-[10px] font-medium">Orders</span>
                        </Link>

                        <Link
                            to="/profile"
                            className={`flex flex-col items-center space-y-1 ${location.pathname === '/profile' ? 'text-primary-600' : 'text-gray-500'}`}
                        >
                            <User className="w-6 h-6" />
                            <span className="text-[10px] font-medium">Profile</span>
                        </Link>
                    </div>
                </nav>
            )}

            <BranchSelectionModal
                isOpen={showBranchModal}
                onClose={() => setShowBranchModal(false)}
                branches={branches}
                franchise={franchise}
            />
        </nav >
    );
}